import { Avatar, findInParty } from "./Avatar.js";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  REVIEW_NOTICE,
  type CampaignImage,
  type FeedbackResponse,
  type GameSession,
  type PlaytestResponse,
  type PlaytestStatus,
  type ReplayEvent,
  type ReplayKind,
  type ReplayNote,
  type ReplayResponse,
  type SessionPlaytestStats,
  type TalkShare,
  type TranscriptLine,
} from "@dm/shared";
import { api } from "./api.js";
import { useGame } from "./game.js";
import { usd } from "./CostsPage.js";

type View = "watch" | "replay" | "dashboard" | "feedback";
const VIEW_KEY = "dm.playtestView";

const secs = (ms: number | null | undefined) => (ms === null || ms === undefined ? "–" : `${(ms / 1000).toFixed(1)} s`);
const clock = (t: number) => {
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = t % 60;
  return `${h ? `${h}:${String(m).padStart(2, "0")}` : m}:${String(s).padStart(2, "0")}`;
};
const when = (iso: string) => new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const timeOnly = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", second: "2-digit" });

/** Host / Co-host / Admin: watch a live session read-only, replay finished ones, the playtest dashboard and player feedback. */
export function PlaytestPage() {
  const { isHost } = useGame();
  const [view, setView] = useState<View>(() => (localStorage.getItem(VIEW_KEY) as View | null) ?? "watch");
  const [replayId, setReplayId] = useState<number | null>(null);
  const go = (v: View) => {
    setView(v);
    localStorage.setItem(VIEW_KEY, v);
  };
  if (!isHost) return <main className="muted">Only the Host, Co-hosts and the Admin can open Playtest.</main>;
  return (
    <main className="playtest">
      <div className="subtabs">
        {([["watch", "Watch live"], ["replay", "Replay"], ["dashboard", "Dashboard"], ["feedback", "Feedback"]] as const).map(([v, label]) => (
          <button key={v} className={view === v ? "active" : ""} onClick={() => go(v)}>{label}</button>
        ))}
      </div>
      {view === "watch" && <WatchLive />}
      {view === "replay" && <Replay initial={replayId} />}
      {view === "dashboard" && <Dashboard onReplay={(id) => { setReplayId(id); go("replay"); }} />}
      {view === "feedback" && <FeedbackList />}
    </main>
  );
}

// ---------------------------------------------------------------- spectator seat

/** Read-only live view. Web viewers are invisible to the DM (it only knows who is in the voice channel). */
function WatchLive() {
  const { campaign, session, playSession, combat, party, rolls, mood, imageVersion, live } = useGame();
  const [lines, setLines] = useState<TranscriptLine[]>([]);
  const [images, setImages] = useState<CampaignImage[]>([]);
  const lastId = useRef(0);
  const box = useRef<HTMLOListElement>(null);
  const sid = playSession?.id ?? null;

  useEffect(() => {
    lastId.current = 0;
    setLines([]);
    if (sid === null) return;
    let stop = false;
    let timer: ReturnType<typeof setTimeout>;
    const tick = async () => {
      try {
        const r = await api.transcript(campaign.id, lastId.current, sid, 500);
        if (stop) return;
        if (r.lines.length) {
          lastId.current = r.lines[r.lines.length - 1]!.id;
          setLines((prev) => [...prev, ...r.lines].slice(-400));
        }
      } catch {
        /* retry */
      }
      if (!stop) timer = setTimeout(tick, 2000);
    };
    void tick();
    return () => {
      stop = true;
      clearTimeout(timer);
    };
  }, [campaign.id, sid]);
  useEffect(() => {
    const el = box.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines.length]);
  useEffect(() => {
    api.images(campaign.id).then((r) => setImages(r.images.slice(0, 4)), () => undefined);
  }, [campaign.id, imageVersion]);

  const sessionRolls = sid === null ? [] : rolls.filter((r) => !playSession || Date.parse(r.createdAt) >= Date.parse(playSession.startedAt)).slice(0, 12);
  return (
    <>
      <p className="watch-status">
        <span className={`dot ${live ? "on" : ""}`} /> {sid === null ? "No session running right now." : `Session ${playSession!.number} · started ${timeOnly(playSession!.startedAt)}`}
        {session && (
          <> · DM {session.botInVoice ? `in #${session.channelName ?? "voice"}` : "not in voice"}{session.paused ? " · paused" : ""}
            {session.botInVoice && ` · ${session.voiceMemberIds.length} in voice`}</>
        )}
        {" "}· mood: {mood}
        <span className="muted small"> · You're watching read-only. The DM and the players can't see you.</span>
      </p>
      <div className="watch-grid">
        <section className="panel watch-transcript">
          <h3>Live transcript</h3>
          {sid === null && <p className="muted">When a session starts, the conversation appears here.</p>}
          <ol ref={box}>
            {lines.map((l) => (
              <li key={l.id} className={`${l.isDm ? "dm" : l.source === "system" ? "system" : "player"}${l.rewound ? " rewound" : ""}`}>
                <span className="muted small">{timeOnly(l.createdAt)}</span> {!l.isDm && <Avatar c={findInParty(party, { name: l.speakerName })} name={l.speakerName} size={18} />} <strong>{l.isDm ? "DM" : l.speakerName}</strong>
                {l.source === "text" && <span className="muted small"> (typed)</span>}: {l.text}
              </li>
            ))}
          </ol>
        </section>
        <div>
          <section className="panel">
            <h3>Combat {combat && <span className="muted small">round {combat.round}</span>}</h3>
            {!combat ? <p className="muted">No combat.</p> : (
              <table className="simple">
                <tbody>
                  {combat.combatants.map((x) => (
                    <tr key={x.id} className={x.id === combat.currentCombatantId ? "current" : ""}>
                      <td>{x.id === combat.currentCombatantId ? "▶" : ""}</td>
                      <td>{x.initiative ?? "–"}</td>
                      <td>{x.kind === "character" && <Avatar c={findInParty(party, { characterId: x.characterId })} name={x.name} size={22} />} {x.name}{x.kind === "monster" ? <span className="muted small"> foe</span> : null}</td>
                      <td>{x.hp}/{x.maxHp}{x.down ? " ☠" : ""}</td>
                      <td className="muted small">{x.conditions.join(", ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
          <section className="panel">
            <h3>Party</h3>
            <table className="simple">
              <tbody>
                {party.map((c) => (
                  <tr key={c.id}><td><Avatar c={c} size={22} /> {c.name}</td><td>{c.currentHp}/{c.maxHp} HP{c.tempHp ? ` +${c.tempHp}` : ""}</td><td className="muted small">{c.ownerName}</td></tr>
                ))}
              </tbody>
            </table>
          </section>
          <section className="panel">
            <h3>Rolls</h3>
            {sessionRolls.length === 0 ? <p className="muted">No rolls this session yet.</p> : (
              <ul className="plain">
                {sessionRolls.map((r) => (
                  <li key={r.id}><strong>{r.characterName ?? r.rollerName}</strong> {r.label ? `${r.label}: ` : ""}{r.formula} = <strong>{r.total}</strong></li>
                ))}
              </ul>
            )}
          </section>
          {images.length > 0 && (
            <section className="panel">
              <h3>Images</h3>
              <div className="watch-images">
                {images.map((im) => <img key={im.id} src={im.url} alt={im.caption || "Image"} title={`${im.caption}${im.shown ? "" : " (hidden from players)"}`} className={im.shown ? "" : "hidden-img"} />)}
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------- replay

const GROUPS: Array<{ id: string; label: string; kinds: ReplayKind[] }> = [
  { id: "talk", label: "Talk", kinds: ["say", "dm", "system"] },
  { id: "rules", label: "Tools & rolls", kinds: ["tool", "roll"] },
  { id: "rewards", label: "XP & loot", kinds: ["award", "loot"] },
  { id: "images", label: "Images", kinds: ["image"] },
  { id: "safety", label: "Rewinds, rulings, appeals", kinds: ["rewind", "ruling", "appeal"] },
];
const KIND_LABEL: Record<ReplayKind, string> = {
  say: "Player", dm: "DM", system: "System", tool: "Tool", roll: "Roll", award: "Award", loot: "Loot", image: "Image",
  rewind: "Rewind", ruling: "Ruling", appeal: "Appeal", note: "Note",
};

function Replay({ initial }: { initial: number | null }) {
  const { campaign, isAdmin, sessionsVersion, fail, party } = useGame();
  const [sessions, setSessions] = useState<GameSession[]>([]);
  const [sid, setSid] = useState<number | null>(initial);
  const [data, setData] = useState<ReplayResponse | null>(null);
  const [on, setOn] = useState<Record<string, boolean>>({ talk: true, rules: true, rewards: true, images: true, safety: true });
  const [q, setQ] = useState("");
  const [slowOnly, setSlowOnly] = useState(false);
  const [pos, setPos] = useState(0);
  const [noteFor, setNoteFor] = useState<string | null>(null);
  const [noteText, setNoteText] = useState("");
  const list = useRef<HTMLOListElement>(null);

  useEffect(() => {
    api.sessions(campaign.id).then((r) => {
      setSessions(r.sessions);
      setSid((cur) => cur ?? r.sessions.find((s) => s.endedAt)?.id ?? r.sessions[0]?.id ?? null);
    }, fail);
  }, [campaign.id, sessionsVersion, fail]);
  const load = () => (sid === null ? Promise.resolve() : api.replay(campaign.id, sid).then(setData, fail));
  useEffect(() => {
    setData(null);
    setPos(0);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign.id, sid]);

  const kinds = useMemo(() => new Set(GROUPS.filter((g) => on[g.id]).flatMap((g) => g.kinds)), [on]);
  const events = useMemo(() => (data?.events ?? []).filter((e) => {
    if (!kinds.has(e.kind)) return false;
    if (slowOnly && !(e.kind === "dm" && (e.totalMs ?? 0) >= 5000)) return false;
    if (q && !`${e.who ?? ""} ${e.text} ${e.detail ?? ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [data, kinds, slowOnly, q]);
  const notesBy = useMemo(() => {
    const m = new Map<string, ReplayNote[]>();
    for (const n of data?.notes ?? []) m.set(n.eventKey, [...(m.get(n.eventKey) ?? []), n]);
    return m;
  }, [data]);
  const maxT = data ? Math.max(1, ...data.events.map((e) => e.t)) : 1;
  const cur = Math.min(pos, Math.max(0, events.length - 1));
  useEffect(() => {
    list.current?.querySelector(`[data-i="${cur}"]`)?.scrollIntoView({ block: "nearest" });
  }, [cur]);
  const seek = (t: number) => {
    const i = events.findIndex((e) => e.t >= t);
    setPos(i < 0 ? events.length - 1 : i);
  };

  const addNote = async (e: ReplayEvent) => {
    if (!sid || !noteText.trim()) return;
    try {
      await api.addReplayNote(campaign.id, sid, e.key, noteText.trim());
      setNoteFor(null);
      setNoteText("");
      await load();
    } catch (err) {
      fail(err);
    }
  };
  const promote = async (n: ReplayNote, to: "campaign" | "global") => {
    try {
      if (to === "campaign") {
        await api.addHomebrew(campaign.id, { kind: "rule", name: n.text.slice(0, 60), summary: n.text.slice(0, 300), details: n.text });
        await api.markReplayNote(campaign.id, n.id, "campaign");
      } else if (isAdmin) {
        await api.addGlobal("rule", n.text);
        await api.markReplayNote(campaign.id, n.id, "global");
      } else {
        await api.suggestGlobal(campaign.id, "rule", n.text);
        await api.markReplayNote(campaign.id, n.id, "suggested");
      }
      await load();
    } catch (err) {
      fail(err);
    }
  };

  return (
    <section className="panel replay">
      <div className="quick-row">
        <label>
          Session{" "}
          <select value={sid ?? ""} onChange={(e) => setSid(Number(e.target.value) || null)}>
            {sessions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.number}: {when(s.startedAt)}{s.endedAt ? "" : " (running)"}
              </option>
            ))}
          </select>
        </label>
        {GROUPS.map((g) => (
          <label key={g.id} className="check-inline">
            <input type="checkbox" checked={!!on[g.id]} onChange={() => setOn((o) => ({ ...o, [g.id]: !o[g.id] }))} /> {g.label}
          </label>
        ))}
        <label className="check-inline" title="DM replies that took 5 seconds or more">
          <input type="checkbox" checked={slowOnly} onChange={() => setSlowOnly(!slowOnly)} /> Slow replies only
        </label>
        <input type="search" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {!data ? <p className="muted">{sid === null ? "No sessions yet." : "Loading…"}</p> : (
        <>
          <div className="replay-scrub">
            <button onClick={() => setPos(Math.max(0, cur - 1))} disabled={cur <= 0}>◀ Prev</button>
            <input type="range" min={0} max={maxT} value={events[cur]?.t ?? 0} onChange={(e) => seek(Number(e.target.value))} />
            <button onClick={() => setPos(Math.min(events.length - 1, cur + 1))} disabled={cur >= events.length - 1}>Next ▶</button>
            <span className="muted small">{events.length ? `${cur + 1} / ${events.length} · ${clock(events[cur]!.t)}` : "No events match"}</span>
          </div>
          <ol className="replay-list" ref={list}>
            {events.map((e, i) => (
              <li key={e.key} data-i={i} className={`rk-${e.kind}${i === cur ? " current" : ""}${e.rewound ? " rewound" : ""}`} onClick={() => setPos(i)}>
                <span className="muted small replay-t" title={timeOnly(e.at)}>{clock(e.t)}</span>
                <span className={`tag rk rk-${e.kind}`}>{KIND_LABEL[e.kind]}</span>
                <span className="replay-text">
                  {e.who && <><Avatar c={findInParty(party, { name: e.who })} name={e.who} size={18} /> <strong>{e.who}: </strong></>}
                  {e.text}
                  {e.kind === "tool" && e.detail && e.detail !== e.text && <span className="muted small"> · {e.detail}</span>}
                  {e.kind === "tool" && e.ok === false && <span className="tag danger">failed</span>}
                  {e.detail && e.kind !== "tool" && e.kind !== "roll" && <span className="muted small"> · {e.detail}</span>}
                  {e.kind === "dm" && (e.totalMs != null || e.firstAudioMs != null) && (
                    <span className={`latency${(e.totalMs ?? 0) >= 5000 ? " slow" : ""}`} title={e.measured ? "Measured by the bot" : "Estimated from transcript times"}>
                      {e.firstAudioMs != null && <>first audio {secs(e.firstAudioMs)} · </>}{e.firstSentenceMs != null && <>first sentence {secs(e.firstSentenceMs)} · </>}reply {secs(e.totalMs)}{e.measured ? "" : " (est.)"}
                      {e.flags?.length ? ` · ${e.flags.join(", ")}` : ""}
                    </span>
                  )}
                </span>
                <button className="link small" onClick={(ev) => { ev.stopPropagation(); setNoteFor(noteFor === e.key ? null : e.key); setNoteText(""); }}>
                  {noteFor === e.key ? "Cancel" : "Note"}
                </button>
                {(notesBy.get(e.key) ?? []).map((n) => (
                  <div key={n.id} className="replay-note" onClick={(ev) => ev.stopPropagation()}>
                    📝 {n.text} <span className="muted small">— {n.byName ?? "someone"}</span>
                    {n.promoted ? <span className="tag">{n.promoted === "campaign" ? "added as campaign rule" : n.promoted === "global" ? "added to global memory" : "suggested to the Admin"}</span> : (
                      <>
                        <button className="small" onClick={() => promote(n, "campaign")}>Add as campaign rule</button>
                        <button className="small" onClick={() => promote(n, "global")}>{isAdmin ? "Add to global memory" : "Suggest for global memory"}</button>
                      </>
                    )}
                    <button className="link small" onClick={() => api.deleteReplayNote(campaign.id, n.id).then(load, fail)}>Delete</button>
                  </div>
                ))}
                {noteFor === e.key && (
                  <div className="replay-note" onClick={(ev) => ev.stopPropagation()}>
                    <input autoFocus value={noteText} maxLength={1000} placeholder="Tuning note, e.g. “Keep descriptions to two sentences in combat”"
                      onChange={(ev) => setNoteText(ev.target.value)} onKeyDown={(ev) => ev.key === "Enter" && void addNote(e)} />
                    <button className="primary small" disabled={!noteText.trim()} onClick={() => addNote(e)}>Save note</button>
                  </div>
                )}
              </li>
            ))}
          </ol>
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- dashboard

function TalkBars({ talk }: { talk: TalkShare[] }) {
  if (!talk.length) return <p className="muted small">Nobody spoke.</p>;
  return (
    <div className="talk-bars">
      {talk.map((t) => (
        <div key={t.userId ?? t.name} className="talk-bar" title={`${t.utterances} lines · ${t.words} words`}>
          <span>{t.name}</span>
          <div><i style={{ width: `${Math.round(t.share * 100)}%` }} /></div>
          <span className="muted small">{Math.round(t.share * 100)}%</span>
        </div>
      ))}
    </div>
  );
}

function Dashboard({ onReplay }: { onReplay: (sessionId: number) => void }) {
  const { campaign, sessionsVersion, fail } = useGame();
  const [idle, setIdle] = useState(120);
  const [d, setD] = useState<PlaytestResponse | null>(null);
  const [open, setOpen] = useState<number | null>(null);
  useEffect(() => {
    api.playtest(campaign.id, idle).then(setD, fail);
  }, [campaign.id, idle, sessionsVersion, fail]);
  if (!d) return <p className="muted">Loading…</p>;
  const t = d.totals;
  const both = d.multiplier !== 1;
  const perHour = (s: { costPerHourUsd: number | null }) =>
    s.costPerHourUsd === null ? "–" : `${usd(s.costPerHourUsd)}/h${both ? ` (players ${usd(s.costPerHourUsd * d.multiplier)}/h)` : ""}`;
  const card = (label: string, value: string, help?: string) => (
    <div className="stat-card" title={help}>
      <div className="muted small">{label}</div>
      <div className="stat-value">{value}</div>
    </div>
  );
  return (
    <>
      <section className="panel">
        <h3>
          All sessions <span className="muted small">({t.sessions} session{t.sessions === 1 ? "" : "s"}, {Math.round(t.minutes / 6) / 10} h)</span>
        </h3>
        <div className="stat-cards">
          {card("Time to first audio", `${secs(t.firstAudioAvgMs)} avg · ${secs(t.firstAudioP90Ms)} p90`, "Player stops talking → the DM starts speaking (a quick acknowledgement counts)")}
          {card("Time to first real sentence", `${secs(t.firstSentenceAvgMs)} avg`, "Player stops talking → the DM's actual answer starts (filler lines don't count)")}
          {card("Full reply time", `${secs(t.replyAvgMs)} avg · ${secs(t.replyP90Ms)} p90`, "Player stops talking → the DM's whole reply is written")}
          {card("Quick acknowledgements", `${t.fillers}`, "Replies where the DM said a short filler line first")}
          {card("Dice pre-drafts used", `${t.drafted}`)}
          {card("Choice pre-drafts", t.choiceOffers ? `${t.choiceHits}/${t.choiceOffers} (${Math.round((100 * t.choiceHits) / t.choiceOffers)}%)` : "–",
            `Options offered vs. answers that used a ready draft. Time saved ≈ ${Math.round(t.choiceSavedMs / 1000)} s`)}
          {card("Repeats / rephrases", `${t.repeats}`, "A player said nearly the same thing again within a minute (the DM may not have heard)")}
          {card("Rewinds", `${t.rewinds}`)}
          {card("Rulings appeals", `${t.appeals}`)}
          {card("Cost per hour (actual)", perHour(t))}
          {card("Unused drafts (waste)", usd(t.wasteUsd), "Speculative drafts the DM threw away (included in the cost)")}
          {card("Player rating", t.ratingAvg === null ? "–" : `${t.ratingAvg.toFixed(1)} ★ (${t.ratings})`)}
        </div>
        <h4>Talk balance</h4>
        <TalkBars talk={t.talk} />
      </section>
      <section className="panel">
        <h3>
          Per session
          <label className="small">
            Idle stretch over{" "}
            <select value={idle} onChange={(e) => setIdle(Number(e.target.value))}>
              {[30, 60, 120, 300, 600].map((s) => <option key={s} value={s}>{s < 60 ? `${s} s` : `${s / 60} min`}</option>)}
            </select>
          </label>
        </h3>
        <div className="table-scroll">
          <table className="simple playtest-table">
            <thead>
              <tr>
                <th>#</th><th>Date</th><th>Length</th><th>Replies</th><th>First audio avg / p90</th><th>Reply avg / p90</th>
                <th>Fillers</th><th>Drafts</th><th>Repeats</th><th>Rewinds</th><th>Appeals</th><th>Idle</th><th>Cost/h</th><th>Waste</th><th>Rating</th><th />
              </tr>
            </thead>
            <tbody>
              {d.sessions.map((s: SessionPlaytestStats) => (
                <Fragment key={s.sessionId}>
                  <tr onClick={() => setOpen(open === s.sessionId ? null : s.sessionId)} className="clickable">
                    <td>{s.number}</td>
                    <td>{when(s.startedAt)}{s.endedAt ? "" : " (running)"}</td>
                    <td>{s.minutes} min</td>
                    <td>{s.dmReplies}{s.measuredReplies < s.dmReplies ? <span className="muted small" title="Replies without bot timing use estimates"> ({s.measuredReplies} timed)</span> : null}</td>
                    <td>{secs(s.firstAudioAvgMs)} / {secs(s.firstAudioP90Ms)}</td>
                    <td>{secs(s.replyAvgMs)} / {secs(s.replyP90Ms)}</td>
                    <td>{s.fillers}</td><td>{s.drafted}</td><td>{s.repeats}</td><td>{s.rewinds}</td><td>{s.appeals}</td>
                    <td title={s.idleGaps.map((g) => `${timeOnly(g.at)}: ${Math.round(g.seconds / 6) / 10} min`).join("\n")}>
                      {s.idleGaps.length}{s.idleGaps.length ? ` (longest ${Math.round(Math.max(...s.idleGaps.map((g) => g.seconds)) / 6) / 10} min)` : ""}
                    </td>
                    <td>{perHour(s)}</td>
                    <td>{usd(s.wasteUsd)}</td>
                    <td>{s.ratingAvg === null ? "–" : `${s.ratingAvg.toFixed(1)} (${s.ratings})`}</td>
                    <td><button className="small" onClick={(e) => { e.stopPropagation(); onReplay(s.sessionId); }}>Replay</button></td>
                  </tr>
                  {open === s.sessionId && (
                    <tr>
                      <td colSpan={17}>
                        <TalkBars talk={s.talk} />
                        <p className="muted small">
                          Cost {usd(s.costUsd)} actual{both ? ` · players pay ${usd(s.playerCostUsd)}` : ""} · unused drafts {usd(s.wasteUsd)}
                        </p>
                        {(s.costByModel ?? []).length > 0 && (
                          <table className="cost-by-model">
                            <thead><tr><th>Model</th><th>Cost</th><th>Calls</th><th title="Share of input tokens served from xAI's prompt cache (cheaper)">Cached input</th></tr></thead>
                            <tbody>
                              {s.costByModel.map((m) => (
                                <tr key={m.model}><td>{m.model}</td><td>{usd(m.usd)}</td><td>{m.calls}</td><td>{m.cachedPct === null ? "—" : `${m.cachedPct}%`}</td></tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {d.sessions.length === 0 && <p className="muted">No sessions yet.</p>}
      </section>
    </>
  );
}

// ---------------------------------------------------------------- feedback

function FeedbackList() {
  const { campaign, fail } = useGame();
  const [d, setD] = useState<FeedbackResponse | null>(null);
  useEffect(() => {
    api.feedbackList(campaign.id).then(setD, fail);
  }, [campaign.id, fail]);
  if (!d) return <p className="muted">Loading…</p>;
  return (
    <section className="panel">
      <h3>Player feedback {d.avg !== null && <span className="muted small">average {d.avg.toFixed(1)} ★</span>}</h3>
      {d.entries.length === 0 ? <p className="muted">No ratings yet. Players are asked after each session (web and Discord).</p> : (
        <table className="simple">
          <thead><tr><th>Session</th><th>Player</th><th>Rating</th><th>What the DM should do better</th><th>From</th></tr></thead>
          <tbody>
            {d.entries.map((e) => (
              <tr key={e.id}>
                <td>{e.sessionNumber}</td>
                <td>{e.userName}</td>
                <td>{e.rating === null ? "–" : "★".repeat(e.rating) + "☆".repeat(5 - e.rating)}</td>
                <td>{e.note || <span className="muted">–</span>}</td>
                <td className="muted small">{e.source === "discord" ? "Discord" : "Web"} · {when(e.at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- for everyone: review notice + quick rating

/** The review notice (once per campaign) and the post-session rating card. */
export function PlaytestPrompts() {
  const { campaign, role, sessionsVersion, endedSessionId } = useGame();
  const [st, setSt] = useState<PlaytestStatus | null>(null);
  const [rating, setRating] = useState<number | null>(null);
  const [note, setNote] = useState("");
  const [thanks, setThanks] = useState(false);
  const [noticeOpen, setNoticeOpen] = useState(false);
  useEffect(() => {
    if (!role) return;
    let stop = false;
    // Just after a session ends, give the server a moment.
    const t = setTimeout(() => api.playtestStatus(campaign.id).then((s) => !stop && setSt(s), () => undefined), sessionsVersion ? 3000 : 0);
    return () => {
      stop = true;
      clearTimeout(t);
    };
  }, [campaign.id, role, sessionsVersion]);
  if (!role || !st) return null;
  const ack = async () => {
    await api.acknowledgeReview(campaign.id).catch(() => undefined);
    setSt({ ...st, acknowledged: true });
    setNoticeOpen(false);
  };
  const send = async (r: number | null, n: string, done: boolean) => {
    if (!st.pending) return;
    await api.sendFeedback(campaign.id, st.pending.sessionId, r, n).catch(() => undefined);
    if (done) {
      setThanks(r !== null || !!n);
      setSt({ ...st, pending: null });
      setRating(null);
      setNote("");
      if (r !== null || n) setTimeout(() => setThanks(false), 3000);
    }
  };
  return (
    <>
      <button className="review-indicator" title={`${REVIEW_NOTICE} (click to read)`} aria-label="Sessions are reviewed: read the notice" onClick={() => setNoticeOpen(true)}>👁</button>
      {(!st.acknowledged || noticeOpen) && (
        <div className="modal-backdrop">
          <div className="modal">
            <h3>About this campaign</h3>
            <p>{REVIEW_NOTICE}</p>
            <p className="muted small">After each session you can rate it 1 to 5 and say what the DM should do better (optional).</p>
            <div className="quick-row"><button className="primary" onClick={ack}>Got it</button></div>
          </div>
        </div>
      )}
      {st.acknowledged && !noticeOpen && st.pending && endedSessionId === null && (
        <div className="feedback-card">
          <div><strong>How was session {st.pending.number}?</strong> <span className="muted small">Only the Host sees ratings.</span></div>
          <div className="stars">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} className={rating !== null && n <= rating ? "on" : ""} aria-label={`${n} star${n === 1 ? "" : "s"}`}
                onClick={() => { setRating(n); void send(n, "", false); }}>★</button>
            ))}
          </div>
          {rating !== null && (
            <input value={note} maxLength={1000} placeholder="What should the DM do better? (optional)" onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && void send(rating, note.trim(), true)} />
          )}
          <div className="quick-row">
            {rating !== null && <button className="primary small" onClick={() => send(rating, note.trim(), true)}>Done</button>}
            {rating === null && <button className="link small" onClick={() => send(null, "", true)}>Skip</button>}
          </div>
        </div>
      )}
      {thanks && <div className="feedback-card">Thanks! The Host will see it.</div>}
    </>
  );
}
